/**
 * The portal's screens and the engagement code each reports on entry (ADR 0012 step 16).
 *
 * <p>One table rather than a literal at each call site, because a code the server does not know
 * refuses the WHOLE batch it rides in — the session start included — and the client never retries.
 * So a typo here is silence, not an error; `screens.test.js` holds every code to the server's own
 * checked-in list, which is what turns it into a failing test instead.</p>
 *
 * <p>Each code is filed server-side under the feature the screen is about (the portal's leaderboards
 * screen counts toward Leaderboards), so these are not all "portal" figures on the Overview.</p>
 */
export const PORTAL_SCREENS = {
  home: 'portal.home_viewed',
  cards: 'portal.cards_viewed',
  claim: 'portal.claim_viewed',
  leaderboards: 'portal.leaderboards_viewed',
  ratings: 'portal.ratings_viewed',
  fantasy: 'portal.fantasy_viewed',
  stats: 'portal.stats_viewed',
} as const;

export type PortalScreenName = keyof typeof PORTAL_SCREENS;

/** Every portal view that reports nothing, and why (ADR 0012 step 29). */
export const UNTRACKED_VIEWS: Record<string, string> = {};

/** What `App.tsx` renders, each declared (ADR 0012 step 29, `screens.coverage.test.js`). */
export const APP_ELEMENTS: Record<
  string,
  { kind: 'view' } | { kind: 'screen'; screen: PortalScreenName } | { kind: 'untracked'; reason: string }
> = {
  PortalShell: { kind: 'view' },
  HomeView: { kind: 'view' },
  LeaderboardsView: { kind: 'view' },
  RatingsView: { kind: 'view' },
  FantasyView: { kind: 'view' },
  StatsView: { kind: 'view' },
  CardsScreen: { kind: 'screen', screen: 'cards' },
  PlayerPicker: {
    kind: 'untracked',
    reason: 'Staff choosing whose portal to read. The portal codes count players reading their own card, not staff looking somebody up.',
  },
  RoleChooser: {
    kind: 'untracked',
    reason: 'Asked before any role is in force, so there is not yet anybody whose use of the portal it would be.',
  },
  CenteredScreen: {
    kind: 'untracked',
    reason: 'The loading and error frame: nothing has been read yet, so there is no screen to have viewed.',
  },
  Spinner: { kind: 'untracked', reason: 'Loading, inside the frame above.' },
  ErrorRetry: {
    kind: 'untracked',
    reason: 'The API could not be reached, and a view report would go to the same API.',
  },
};
